import { UserAccountVm } from './../_models/user';
import { BlogTopicVm } from './../_models/blog';
import { OhiFedExUnitVm } from './../_models/ohiUnitVm';
import { AccountUpdateRequest } from './../_models/accountUpdateRequest';
import { ApiResponseBase } from './../_models/apiResponse';
import { DigitalResourceVm, DigitalResourceSaveResponse, DigitalResourceLookupResponse, DigitalResourceShortVm, OhiUnitVm } from './../_models/digitalReource';
import { WebinarSaveResponse } from './../_models/webinarSaveResponse';
import { WebinarVm } from '@app/_models/webinar';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { UserShortVm, User, AuthenticateResponse } from '@app/_models/user';
import { LookupItemVm } from './../_models/lookupItem';
import { BlogShortVm, BlogVm, BlogSaveResponse } from '@app/_models/blog';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private lookupEndpoint = `${environment.apiUrl}/api/lookup/`;
  private userEndpoint = `${environment.apiUrl}/api/users/`;
  private universityEndpoint = `${environment.apiUrl}/api/ohiUniversity/`;
  private acceleratorEndpoint = `${environment.apiUrl}/api/accelerator/`;
  private uploadEndpoint = `${environment.apiUrl}/api/Upload/`;
  private fedexEndpoint = `${environment.apiUrl}/api/fedex/`;

  constructor(private http: HttpClient) { }

  /* Users */
  login(user: User): Observable<AuthenticateResponse> {
    return this.http.post<AuthenticateResponse>(`${this.userEndpoint}authenticate`, user);
  }

  updateAccount(updateRequest: AccountUpdateRequest): Observable<ApiResponseBase> {
    return this.http.post<ApiResponseBase>(`${this.userEndpoint}updateAccount`, updateRequest);
  }

  getUserList(): Observable<UserShortVm[]> {
    return this.http.get<UserShortVm[]>(`${this.userEndpoint}getUserList`);
  }

  getUser(id: number): Observable<any> {
    return this.http.get<any>(`${this.userEndpoint}getUser/${id}`);
  }

  updateRole(userAccountId: number, roleId: number, isOn: boolean): Observable<ApiResponseBase> {
    return this.http.get<ApiResponseBase>(`${this.userEndpoint}UpdateRole?userAccountId=${userAccountId}&roleId=${roleId}&isOn=${isOn}`);
  }

  updatePaService(userId: number, serviceId: number, isOn: boolean): Observable<ApiResponseBase> {
    return this.http.get<ApiResponseBase>(`${this.acceleratorEndpoint}updatePaServices?userId=${userId}&serviceId=${serviceId}&isOn=${isOn}`);
  }


  toggleAccountApproval(account: UserAccountVm): Observable<ApiResponseBase> {
    return this.http.post<ApiResponseBase>(`${this.userEndpoint}toggleAccountApproval`, account);
  }

  /* Lookups */
  getRoles(): Observable<LookupItemVm[]> {
    return this.http.get<LookupItemVm[]>(`${this.lookupEndpoint}getRoles/`);
  }

  getPaServices(): Observable<LookupItemVm[]> {
    return this.http.get<LookupItemVm[]>(`${this.lookupEndpoint}getPaServices/`);
  }

  getPaServicesTree(): Observable<any[]> {
    return this.http.get<any[]>(`${this.lookupEndpoint}GetPaServicesTree`);
  }
  

  getOhiUnits():Observable<OhiUnitVm[]> {
    return this.http.get<OhiUnitVm[]>(`${this.lookupEndpoint}getOhiUnits/`);
  }
  
  getOhiUnit(id: number):Observable<OhiUnitVm> {
    return this.http.get<OhiUnitVm>(`${this.lookupEndpoint}getOhiUnit/${id}`);
  }

  getBlogTopics(): Observable<BlogTopicVm[]> {
    return this.http.get<BlogTopicVm[]>(`${this.lookupEndpoint}getBlogTopics/`);
  }

  /* Webinars */
  getWebinar(id: number): Observable<WebinarVm> {
    return this.http.get<WebinarVm>(`${this.universityEndpoint}GetWebinar/${id}`);
  }
  getWebinarList(): Observable<WebinarVm[]> {
    return this.http.get<WebinarVm[]>(`${this.universityEndpoint}getWebinarListAsync`);
  }

  getWebinarThumbnailImages(): Observable<string[]> {
    return this.http.get<string[]>(`${this.universityEndpoint}getWebinarThumbnailImages`);
  }

  saveWebinar(webinar: WebinarVm): Observable<WebinarSaveResponse> {
    return this.http.post<WebinarSaveResponse>(`${this.universityEndpoint}SaveWebinar`, webinar);
  }

  /* Digital Resources */
  getDigitalResourcesLookups(): Observable<DigitalResourceLookupResponse> {
    return this.http.get<DigitalResourceLookupResponse>(`${this.universityEndpoint}getDigitalResourcesLookupsAsync`);
  }

  getDigitalResourceList(): Observable<DigitalResourceShortVm[]> {
    return this.http.get<DigitalResourceShortVm[]>(`${this.universityEndpoint}getDigitalResourceListAsync`);
  }

  getDigitalResource(id: number): Observable<DigitalResourceVm> {
    return this.http.get<DigitalResourceVm>(`${this.universityEndpoint}getDigitalResourceAsync/${id}`);
  }

  saveDigitalResource(resource: DigitalResourceVm): Observable<DigitalResourceSaveResponse> {
    return this.http.post<DigitalResourceSaveResponse>(`${this.universityEndpoint}saveDigitalResource`, resource);
  }

/* Blogs */
  getBlogsBase():Observable<BlogShortVm[]> {
    return this.http.get<BlogShortVm[]>(`${this.universityEndpoint}getBlogsBase`);
  }

  getBlog(id:number):Observable<BlogVm> {
    return this.http.get<BlogVm>(`${this.universityEndpoint}getBlog?blogId=${id}`);
  }

  saveBlog(blog: BlogVm):Observable<BlogSaveResponse>{
    return this.http.post<BlogSaveResponse>(`${this.universityEndpoint}saveBlog`, blog);
  }

  turnBlog(id: number, active: boolean): Observable<ApiResponseBase> {
    return this.http.get<ApiResponseBase>(`${this.universityEndpoint}turnBlog?blogId=${id}&active=${active}`);
  }

  setBlogTopic(id: number, topicId: number): Observable<ApiResponseBase> {
    return this.http.get<ApiResponseBase>(`${this.universityEndpoint}setBlogTopic?blogId=${id}&topicId=${topicId}`);
  }

  /* File Upload */
  uploadDigitalResource(formData: any, unitTitle: string) {
    return this.http.post<any>(`${this.uploadEndpoint}DigitalResource?unitTitle=${unitTitle}`, formData, {
      reportProgress: true, observe: 'events'
    });
  }
  
  uploadBlogImage(formData: any) {
    return this.http.post<any>(`${this.uploadEndpoint}UploadBlogImage`, formData, {
      reportProgress: true, observe: 'events'
    });
  }

  updateBlogImage(blogId: number, uri: string) {
    return this.http.get<any>(`${this.universityEndpoint}UpdateBlogImage?blogId=${blogId}&uri=${uri}`);
  }


  GetBlogImages(): Observable<string[]> {
    return this.http.get<string[]>(`${this.universityEndpoint}GetBlogImages`);
  }

  /* FedEx */

  getFedExUnit(ohiUnitId: number): Observable<OhiFedExUnitVm> {
    return this.http.get<OhiFedExUnitVm>(`${this.fedexEndpoint}getFedExUnit/?ohiUnitId=${ohiUnitId}`);
  }

  saveFedExUnit(data: OhiFedExUnitVm): Observable<OhiFedExUnitVm> {
    return this.http.post<OhiFedExUnitVm>(`${this.fedexEndpoint}saveFedExUnit`, data);
  }
}